import { SMSBatch } from '../hooks/useSMSHistory';
import { ChartDataPoint as GlobalChartDataPoint } from '../types';
import { transformChartDataOptimized } from './chartDataTransformer';
import { getFeatureFlags } from './featureFlags';

export interface ChartDataPoint {
  time: string;
  volume: number;
  cost: number;
  successRate: number;
}

export interface RechartsDataPoint {
  [key: string]: string | number | Date;
}

/**
 * Transforms batched SMS history data into chart-friendly format for trend analysis
 */
export function transformBatchedHistoryToChartData(batches: SMSBatch[]): ChartDataPoint[] {
  if (!batches || batches.length === 0) {
    return [];
  }

  return batches.map(batch => ({
    time: formatTimeRangeForChart(batch.timeRange?.start || new Date().toISOString()),
    volume: batch.summary?.totalMessages || 0,
    cost: batch.summary?.totalCost || 0,
    successRate: batch.summary?.successRate || 0
  }));
}

/**
 * Formats time range for chart display
 */
function formatTimeRangeForChart(startTime: string): string {
  const date = new Date(startTime);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}

/**
 * Calculates moving averages for trend smoothing
 */
export function calculateMovingAverage(data: ChartDataPoint[], windowSize: number = 3): ChartDataPoint[] {
  if (data.length < windowSize) {
    return data;
  }

  const result: ChartDataPoint[] = [];

  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    const end = i + 1;
    const window = data.slice(start, end);

    const avgVolume = window.reduce((sum, item) => sum + item.volume, 0) / window.length;
    const avgCost = window.reduce((sum, item) => sum + item.cost, 0) / window.length;
    const avgSuccessRate = window.reduce((sum, item) => sum + item.successRate, 0) / window.length;

    result.push({
      time: data[i].time,
      volume: Math.round(avgVolume),
      cost: Math.round(avgCost * 100) / 100, // Round to 2 decimal places
      successRate: Math.round(avgSuccessRate * 10) / 10 // Round to 1 decimal place
    });
  }

  return result;
}

/**
 * Transforms chart data for Recharts
 */
export function transformChartDataForRecharts(
  data: any[],
  chartType: 'line' | 'bar' | 'pie' | 'radar' | 'scatter'
): {
  data: RechartsDataPoint[];
  config?: any;
  validation?: any;
} {
  try {
    const result = transformChartDataOptimized(data as any, chartType, {
      batchSize: 1000,
      enableValidation: false
    });

    return {
      data: result.config.data,
      config: result.config,
      validation: result.validation
    };
  } catch (error) {
    console.warn('Recharts data transformation failed:', error);
    return { data: [] };
  }
}

/**
 * Performance monitoring for chart operations
 */
export function withChartPerformanceMonitoring<T>(
  operationName: string,
  operation: () => T
): T {
  const flags = getFeatureFlags();
  if (!flags.enablePerformanceMonitoring) {
    return operation();
  }

  const startTime = performance.now();
  const result = operation();
  const duration = performance.now() - startTime;

  console.log(`[CHART-PERF] ${operationName}: ${duration.toFixed(2)}ms`);

  return result;
}

/**
 * Filters chart data by date range
 */
export function filterChartDataByDateRange<T extends { time: string }>(
  data: T[],
  startDate: string,
  endDate: string
): T[] {
  const start = new Date(startDate);
  const end = new Date(endDate);

  return data.filter(point => {
    const pointDate = new Date(point.time);
    if (isNaN(pointDate.getTime())) return true; // Fallback for formatted strings
    return pointDate >= start && pointDate <= end;
  });
}